import { renterListingOf } from '../quote/renterListing.js'
import { readinessOf } from './readiness.js'
import { statusOf } from '../property/homeStatus.js'
import { calendarCheck, CHECK_DAYS } from './calendarCheck.js'
import { priceListing, averageMonthlyCents } from './listingPrice.js'
import { verdictFor, MID_TERM_MINIMUM_NIGHTS } from '../availability/search.js'
import { buildMirror } from '../availability/mirror.js'
import { moveInDays, moveOutDays } from '../availability/openings.js'
import { availabilityNote } from './availabilityNote.js'
import { assignmentsFrom } from '../pricing/strategy.js'
import { fromMonthlyCents } from './fromPrice.js'
import { goodToKnowOf } from './goodToKnow.js'
import { nightsBetween } from '../pricing/stay.js'
import { listingUnitFor, roomTypeListingName } from '../property/roomTypeListings.js'

/**
 * THE LISTING A RENTER CAN OPEN, DECIDED ON THE SERVER. (MTR #88, #172.)
 *
 * The Product Owner, 2026-09-28/29: the listing is our own direct-booking site without the
 * payment — she picks dates, sees the price, sends an inquiry. The price is shown, so it is worked
 * out HERE, by the same `priceListing` Create quote agrees with to the cent, and only the figures
 * she is meant to read leave. The rent floor, the strategy, the minimum, the owner's settings stay
 * on the server; that is the whole reason this is not simply the staff page with the login removed.
 *
 * Which homes can be opened is decided here too, at the moment of asking: a home that stopped
 * being listable this morning is not listable at lunch because somebody kept yesterday's link.
 */

/* ── the address ───────────────────────────────────────────────────────────────────────────── */

const STAY_PATH = '/stay/'

/*
  A HOME'S ADDRESS IS ITS NAME, FOR PEOPLE, AND THE END OF ITS ID, FOR US.

  `/stay/ballard-view-1br-3f9c2a71`. Only the eight characters at the end are read, so renaming a
  home in Guesty does not break a link somebody sent last week, and the words are there so the link
  says what it is in a text message. The END of the id rather than the start: these are v7 uuids,
  whose start is a timestamp, so homes imported in one batch share it.
*/
const KEY_LENGTH = 8
const keyOf = (unit) => String(unit?.id ?? '').replace(/-/g, '').slice(-KEY_LENGTH).toLowerCase()
const slugWords = (s) => String(s ?? '').toLowerCase().normalize('NFKD').replace(/[^\p{L}\p{N}]+/gu, '-')
  .replace(/^-+|-+$/g, '').slice(0, 60).replace(/-+$/, '')

export function stayPathOf(unit) {
  const key = keyOf(unit)
  if (key.length < KEY_LENGTH) return null
  /*
    THE HOME'S OWN NAME, DIRECTLY (#402). This used to read
    `renterListingOf(unit).listing?.title`, which has never returned anything: `listingForRenter`
    carries no `title`, so the fallback to `unit.name` was always the answer. Since #402 that
    detour also matters — `renterListingOf` now refuses a home whose listing has not been loaded,
    and building a link is not a reason to load one.
  */
  const words = slugWords(unit?.name)
  return `${STAY_PATH}${words ? `${words}-` : ''}${key}`
}

/*
  /stay/<words>-<key>, or /stay/<words>-<key>/<name> — an ambassador's link to the same home (Ambassador
  Program, 2026-10-05). The name has the shape migration 0056 allows, in either case; anything else is not a page.
*/
const STAY_PATH_SHAPE = /^\/stay\/(?:[\p{L}\p{N}-]*-)?([0-9a-f]{8})(?:\/([A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){1,29}))?\/?$/u

/** The key a `/stay/…` path names, or null. Anything that is not exactly that shape is not a home. */
export function stayKeyFromPath(path) {
  const m = STAY_PATH_SHAPE.exec(String(path ?? ''))
  return m ? m[1] : null
}

/** `{ key, code }` when the path is a home's address with an ambassador's name after it, else null. */
export function ambassadorFromPath(path) {
  const m = STAY_PATH_SHAPE.exec(String(path ?? ''))
  /* Phones capitalise what people type (review of PR 2): Holly is holly. The home's key is never folded. */
  return m && m[2] ? { key: m[1], code: m[2].toLowerCase() } : null
}

/** The one home a key names. Two homes sharing a key is treated as none: a link must never guess. */
export function homeForKey(units = [], key) {
  const hits = units.filter((u) => keyOf(u) === key)
  /* A link to one Suites room opens its room type's listing (2026-10-07): old links keep working. */
  return hits.length === 1 ? listingUnitFor(hits[0], units) : null
}

/* ── what she may see ──────────────────────────────────────────────────────────────────────── */

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const addDays = (iso, n) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10)

/**
 * Is this home one a renter may open today? The staff Listing page's own rule (`readinessOf` with
 * the calendar judged), so a home the team sees as "ready" is exactly a home the public can open.
 */
function publicReadiness(unit, settings, { calendar, today, status = null }) {
  const mirror = buildMirror({ days: calendar.days, windowStart: calendar.from, windowEnd: calendar.to })
  return readinessOf(unit, settings, { calendar: calendarCheck(unit, mirror, { from: today }), status })
}

/**
 * The home as the page draws it — and nothing the page does not draw. Built field by field rather
 * than by deleting the private ones, so a column added to the unit tomorrow is private until
 * somebody decides otherwise.
 */
function publicHomeOf(unit, readiness, backups = {}) {
  const { area, listing: full } = renterListingOf(unit)
  /*
    NOT `access`. It is written for a guest who has already booked — how to get in — and the text
    redactor cannot be trusted to find every way staff write a code ("combo 4-4-1-7", "lockbox by
    the gate, 4417"). Quote pages sit behind a token; this page is public. (Review of #88.)
  */
  /*
    EACH PHOTO CARRIES OUR OWN COPY, WHEN WE HAVE ONE (#262). The vendor's URL stays first; the page
    swaps to `backupUrl` only when the vendor's image does not load. A photo we hold no copy of goes
    out exactly as before.
  */
  const photos = (full?.photos ?? []).map((p) => (backups[p.url] ? { ...p, backupUrl: backups[p.url] } : p))
  const listing = full ? { ...full, access: null, photos } : full
  return {
    path: stayPathOf(unit),
    /* A Suites room type is named for the type, not for the one room that speaks for it (2026-10-07). */
    name: unit.roomTypeId ? roomTypeListingName(unit) : listing?.title ?? unit.name ?? null,
    city: unit.city ?? null,
    area,
    listing,
    bedrooms: unit.bedrooms ?? null,
    bathrooms: unit.bathrooms ?? null,
    accommodates: unit.accommodates ?? null,
    amenities: unit.amenities ?? [],
    offeredCleaning: readiness.offeredCleaning,
  }
}

/**
 * The price she is shown, and nothing it was made from.
 *
 * `priceListing` returns what a quote needs: the strategy's name and id, the floor the rent was
 * held above, every adjustment step. A renter's page needs the months, the cleaning, the deposit
 * and the total — so those are copied out by name, and that is all that leaves.
 */
function publicPriceOf(price) {
  if (!price) return null
  const o = price.option
  return {
    chosenId: price.chosenId,
    choices: price.choices.map((c) => ({ id: c.id, label: c.label, visits: c.visits, amountCents: c.amountCents })),
    option: o ? {
      ok: Boolean(o.ok),
      priced: Boolean(o.priced),
      /*
        NEVER THE ENGINE'S OWN WORDS. A refusal is written for the person pricing — "brings rent to
        $22,811.40, below this home's minimum of $25,200.00. The strategy and the home disagree" —
        and copying it out would hand a renter the floor and the strategy the rest of this function
        keeps back. (Independent review of #88.) She is told only that a person will price it.
      */
      refusals: (o.refusals ?? []).length
        ? [{ message: 'These dates need a person to price them — send an inquiry and we’ll reply with a quote.' }]
        : [],
      renterSegments: (o.renterSegments ?? []).map((s) => ({ label: s.label, nights: s.nights ?? null, rentCents: s.rentCents })),
      depositCents: o.depositCents ?? null,
      totalCents: o.totalCents ?? null,
      averageMonthlyCents: o.priced ? averageMonthlyCents(o) : null,
    } : null,
  }
}

/* ── the two answers the server gives ──────────────────────────────────────────────────────── */

/**
 * `world` is what the listing server loaded: units, effective settings by unit, strategies, deposit
 * policies. `calendar` is { days, from, to } for this one home — at least CHECK_DAYS from today, the
 * window the staff Listing page judges readiness over. A home that is not listable today
 * is answered exactly as a home that does not exist — the link says nothing about what we have.
 */
export function answerHome({ world, key, calendar, today, preview = false }) {
  const unit = homeForKey(world.units, key)
  if (!unit) return { statusCode: 404, body: { status: 'not_found' } }
  const settings = world.settingsByUnit[unit.id] ?? null
  /* Unlisted, deactivated or deleted here is a page that does not exist (#199). */
  const status = statusOf(unit, { statuses: world.homeStatuses ?? {}, settings })
  const readiness = publicReadiness(unit, settings, { calendar, today, status })
  /*
    THE STAFF PREVIEW ASKS THIS SAME QUESTION (#276; the Product Owner, 2026-10-01: one page, not
    two). It gets the very home a renter gets, and — because it is staff, signed in — two things
    more: a home that is not ready yet is still answered, and the answer says whether renters can
    open it and, if not, what it is missing. Nothing else differs, so nothing else can drift.
  */
  if (!readiness.listable && !preview) return { statusCode: 404, body: { status: 'not_found' } }
  const home = publicHomeOf(unit, readiness, world.photoBackups ?? {})
  /* Utilities, lease, pets, parking, moving in, house rules — as sentences only (goodToKnow.js). */
  home.goodToKnow = goodToKnowOf(settings)
  /* "From $X / month" before she picks dates — the same figure as the card she came from (fromPrice.js). */
  const strategies = world.strategies ?? []
  home.fromMonthlyCents = fromMonthlyCents({ unit, settings, offered: readiness.offeredCleaning, today,
    strategy: strategies.find((s) => s.id === assignmentsFrom(strategies)[unit.id]) ?? null, policies: world.depositPolicies ?? [] })
  /* The loop, its cover and the floor plan — only what exists (#298). Absent: no key at all. */
  const media = world.mediaByUnit?.[unit.id]
  if (media && (media.tour || media.floorPlans?.length)) home.media = { ...media } /* a copy: the world is shared across requests */
  if (preview) home.preview = { unitId: unit.id, listable: readiness.listable, missing: readiness.missing }
  return { statusCode: 200, body: { status: 'ok', home }, unit, readiness }
}

/** The eight characters a home's public address ends in — what every /stay and preview question is keyed by. */
export const stayKeyOf = keyOf

/* The longest stay the page prices. Beyond this the answer is a conversation, not a figure. */
const LONGEST_PRICED_NIGHTS = 730

/** How far the server reads a calendar for one home: the readiness window, or past her check-out. */
export function calendarWindowFor(today, checkOut = null) {
  const readinessEnd = addDays(today, CHECK_DAYS - 1)
  const stayEnd = checkOut && ISO_DAY.test(checkOut) ? addDays(checkOut, 1) : readinessEnd
  const cap = addDays(today, 799)
  const to = [readinessEnd, stayEnd].sort().at(-1)
  return { from: today, to: to > cap ? cap : to }
}

/* ── which days she can pick (mtr#214) ─────────────────────────────────────────────────────── */

/**
 * How far ahead the page offers move-in days.
 *
 * The same 180 the staff page judges a calendar over, so "this home is ready" and "these are the
 * days you can pick" are measured across the same stretch of time and cannot disagree.
 */
export const OPENINGS_DAYS = CHECK_DAYS

/** The longest stay the page offers a move-out day for. Past a year it is a conversation. */
export const OPENINGS_LONGEST_NIGHTS = 365

/**
 * The calendar this question needs — further than a price needs.
 *
 * A move-in day on the last offered date still has to have its 30 nights AND its departure day
 * read, so the window runs past the last day offered, not to it. Without those extra 31 days every
 * move-in day in the final month would come back "we don't know" and quietly vanish from the
 * calendar — the same class of mistake as reporting an unfetched night as booked.
 */
export function openingsWindowFor(today, moveIn = null) {
  const cap = addDays(today, 799)
  const to = moveIn && ISO_DAY.test(moveIn)
    ? addDays(moveIn, OPENINGS_LONGEST_NIGHTS + 1)
    : addDays(today, OPENINGS_DAYS + MID_TERM_MINIMUM_NIGHTS + 1)
  return { from: today, to: to > cap ? cap : to }
}

/**
 * THE DAYS SHE CAN PICK, worked out here and sent as dates. (MTR #214.)
 *
 * WHY THIS SENDS DATES WHEN #88 DECIDED THE CALENDAR STAYS ON THE SERVER.
 *
 * That decision stands and this does not breach it. What #88's review kept back was the booking
 * RECORD and the pricing internals — "occupied 4 to 10 Oct" is who else is staying, the floor and
 * the strategy are ours — and none of that leaves here. What leaves is the answer to the only
 * question a renter is entitled to ask: which days may I choose. She can already get it one date at
 * a time, because the price box answers "these dates aren't available" for every pair she tries;
 * all this changes is that she is told before she guesses instead of after, which is what Airbnb,
 * Furnished Finder and Zillow all do and what the Product Owner asked for after losing a renter to
 * the third guess.
 *
 * WITHOUT `moveIn` it answers the first question — which days may I move in. WITH one it answers the
 * second — having picked that day, when may I leave. Two questions rather than one answer holding
 * every combination, because the whole cross-product is 180 × 365 verdicts for a renter who will
 * read one row of it.
 */
/**
 * THE SAME DAYS, WORKED OUT FROM A CALENDAR ALREADY IN HAND (#274). The staff preview holds the whole
 * mirror, so it asks nobody: the same rules, the same limits, the same shape the server answers with,
 * so the preview draws exactly the calendar a renter gets.
 */
export function openingsFromMirror({ unit, mirror, today, moveIn = null }) {
  if (!unit?.id || !mirror || !today) return null
  if (moveIn) {
    const out = moveOutDays(unit, mirror, { moveIn, days: OPENINGS_LONGEST_NIGHTS })
    return { status: 'ok', moveIn, moveOut: { open: out.open, needsOwnerOk: out.needsOwnerOk } }
  }
  const days = moveInDays(unit, mirror, { from: today, days: OPENINGS_DAYS })
  return { status: 'ok', from: today, through: days.lastDayWeCanSpeakFor,
    moveIn: { open: days.open, needsOwnerOk: days.needsOwnerOk }, minimumNights: MID_TERM_MINIMUM_NIGHTS }
}

/**
 * THE STAFF PREVIEW'S CALENDAR, READ OVER THE SAME NIGHTS THE RENTER ROUTE READS (#274, review of
 * #275): one home, `openingsWindowFor` from the Seattle day — never the homes list's shorter mirror.
 */
export async function openingsFromHome({ unit, today, moveIn = null, homeCalendar }) {
  if (!unit?.id || !today || typeof homeCalendar !== 'function') return null
  const w = openingsWindowFor(today, moveIn)
  const days = await homeCalendar({ propertyId: unit.id, from: w.from, to: w.to, roomTypeId: unit.roomTypeId ?? null })
  const mirror = buildMirror({ days, windowStart: w.from, windowEnd: w.to })
  return openingsFromMirror({ unit, mirror, today, moveIn })
}

export function answerOpenings({ world, key, calendar, today, moveIn = null, preview = false }) {
  const home = answerHome({ world, key, calendar, today, preview })
  if (home.statusCode !== 200) return home
  const { unit } = home
  const mirror = buildMirror({ days: calendar.days, windowStart: calendar.from, windowEnd: calendar.to })

  if (moveIn) {
    if (!ISO_DAY.test(String(moveIn)) || moveIn < today) {
      return { statusCode: 400, body: { status: 'invalid_request', errors: ['moveIn must be a date today or later'] } }
    }
    const out = moveOutDays(unit, mirror, { moveIn, days: OPENINGS_LONGEST_NIGHTS })
    return { statusCode: 200, body: { status: 'ok', moveIn, moveOut: { open: out.open, needsOwnerOk: out.needsOwnerOk } } }
  }

  const days = moveInDays(unit, mirror, { from: today, days: OPENINGS_DAYS })
  return {
    statusCode: 200,
    body: {
      status: 'ok',
      from: today,
      /*
        NOT THE END OF THE WINDOW WE READ. The last day we can honestly say anything about, which is
        earlier whenever the sync has not reached as far as we looked. The page says "beyond here,
        ask us" at this date rather than drawing months it would have to refuse.
      */
      through: days.lastDayWeCanSpeakFor,
      moveIn: { open: days.open, needsOwnerOk: days.needsOwnerOk },
      minimumNights: MID_TERM_MINIMUM_NIGHTS,
    },
  }
}

/**
 * The price for her dates, and what we can honestly say about whether the home is free.
 *
 * Only a sentence leaves, never the calendar or the reason behind it: "booked 4–10 Oct" is the
 * home's booking record, and a renter is told whether HER dates work, not who else is staying.
 * A night nobody has read is never "available" (#172) — `availabilityNote` says "we'll confirm",
 * and a stay reaching past what the server could load is said the same way rather than "checking",
 * which on a page with nothing more to fetch would spin for ever.
 */
export function answerPrice({ world, key, calendar, today, now, checkIn, checkOut, cleaning, preview = false }) {
  const home = answerHome({ world, key, calendar, today, preview })
  if (home.statusCode !== 200) return home
  const errors = []
  if (!ISO_DAY.test(String(checkIn ?? '')) || !ISO_DAY.test(String(checkOut ?? ''))) errors.push('checkIn and checkOut must be dates (YYYY-MM-DD)')
  else if (!(checkOut > checkIn)) errors.push('checkOut must be after checkIn')
  else if (checkIn < today) errors.push('checkIn is in the past')
  else if (nightsBetween(checkIn, checkOut) > LONGEST_PRICED_NIGHTS) errors.push(`stays longer than ${LONGEST_PRICED_NIGHTS} nights are priced by a person`)
  if (errors.length) return { statusCode: 400, body: { status: 'invalid_request', errors } }

  const { unit, readiness } = home
  const strategies = world.strategies ?? []
  const assignments = assignmentsFrom(strategies)
  const strategy = strategies.find((s) => s.id === assignments[unit.id]) ?? null
  /*
    NOTHING TO PRICE WITH, NOTHING PRICED. Only a staff preview of a home that is not ready can get
    here without a rent or a cleaning to offer — a listable home always has both — and the page then
    shows what a renter would: no figure, rather than one made from a blank.
  */
  const settings = world.settingsByUnit[unit.id] ?? null
  const price = readiness.offeredCleaning.length > 0 && settings?.monthlyRent != null
    ? priceListing({
      unit, settings, strategy, checkIn, checkOut, cleaning,
      offered: readiness.offeredCleaning, asOf: now, policies: world.depositPolicies ?? [],
    })
    : null

  const mirror = buildMirror({ days: calendar.days, windowStart: calendar.from, windowEnd: calendar.to })
  let verdict = null
  try { verdict = verdictFor(unit, mirror, { checkIn, checkOut }) } catch { verdict = null }
  const note = availabilityNote({ verdict, loading: false })
  const availability = !note || note.tone === 'wait'
    ? { tone: 'owner', text: 'We’ll confirm these dates with you.' }
    : { tone: note.tone, text: note.text }

  return { statusCode: 200, body: { status: 'ok', price: publicPriceOf(price), availability } }
}
